import { useAppSelector } from "hooks/storeHooks"
import { type FC } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, Link as RouterLink } from "react-router-dom"
import { ChartPie, CircleHelp, Code, LayoutGrid, Loader2, Palette, Rotate3d } from "lucide-react"
import { NavSectionCategories } from "./NavSectionCategories"
import { type INavSections, type INavSectionsItems } from "./types"
import { LanguagePicker } from "./LanguageSelector"
import { DemoUserButton } from "./DemoUserButton"
import { useBillingPortal } from "hooks/useBillingPortal"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { TintMark } from "@/components/TintMark/TintMark"
import { hasAnyRequiredGroup } from "utils/userGroups"
import { isTryOnCategory } from "@/features/home/categoryLinks"
import { selectCategoriesLoading, selectSelectedCategories } from "store/product/productSlice"

const VIRTUAL_TRY_ON_SECTION_ID = "virtual-try-on"

interface BillingPortalNavOptions {
  canManageBilling: boolean
  isPortalLoading: boolean
  openBillingPortal: () => Promise<void>
}

interface NavSectionVisibility {
  show3DViewer: boolean
  showTryOn: boolean
}

// Sections appear progressively as the merchant picks categories on Home;
// Home itself and the Info section are unconditional.
const getTranslatedNavSections = (
  t: (key: string) => string,
  billingPortal: BillingPortalNavOptions,
  visibility: NavSectionVisibility
): INavSections[] => [
  {
    title: "",
    items: [
      {
        to: "/home",
        activePaths: ["/home"],
        label: t("nav.home"),
        icon: <LayoutGrid className="h-4 w-4" />,
        testId: "nav-home",
      },
    ],
  },
  ...(visibility.showTryOn
    ? [
        {
          id: VIRTUAL_TRY_ON_SECTION_ID,
          title: t("nav.sectionTryOn"),
          items: [
            {
              to: "/integration-info",
              activePaths: ["/integration-info"],
              label: t("nav.integration"),
              icon: <Code className="h-4 w-4" />,
              testId: "nav-integration-info",
            },
          ],
        },
      ]
    : []),
  ...(visibility.show3DViewer
    ? [
        {
          title: t("nav.section3dViewer"),
          items: [
            {
              to: "/3d-viewer",
              activePaths: ["/3d-viewer", "/3d-viewer/add", "/3d-viewer/edit"],
              label: t("nav.products"),
              icon: <Rotate3d className="h-4 w-4" />,
              testId: "nav-3d-products",
            },
            {
              to: "/3d-viewer/integration",
              activePaths: ["/3d-viewer/integration"],
              label: t("nav.integration"),
              icon: <Code className="h-4 w-4" />,
              testId: "nav-3d-integration",
            },
          ],
        },
      ]
    : []),
  {
    title: t("nav.info"),
    items: [
      ...(billingPortal.canManageBilling
        ? [
            {
              to: "",
              activePaths: [],
              label: t("nav.usageBilling"),
              icon: <ChartPie className="h-4 w-4" />,
              onClick: () => void billingPortal.openBillingPortal(),
              loading: billingPortal.isPortalLoading,
              testId: "nav-billing",
            },
          ]
        : []),
      {
        to: "/ui-customization",
        activePaths: ["/ui-customization"],
        label: t("nav.widgetUiSettings"),
        icon: <Palette className="h-4 w-4" />,
        testId: "nav-ui-customization",
      },
      {
        to: "https://www.banuba.com/support-tint",
        activePaths: [""],
        label: t("nav.support"),
        icon: <CircleHelp className="h-4 w-4" />,
        target: "_blank",
        testId: "nav-support",
      },
    ],
  },
]

const isExternalUrl = (url: string) => /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(url)

const findActiveNavPath = (pathname: string, items: Pick<INavSectionsItems, "activePaths">[]) => {
  let best: string | null = null

  for (const item of items)
    for (const activePath of item.activePaths)
      if (
        activePath &&
        (pathname === activePath || pathname.startsWith(`${activePath}/`)) &&
        (best === null || activePath.length > best.length)
      )
        best = activePath

  return best
}

interface NavbarProps {
  collapsed?: boolean
  onToggle?: () => void
}

export const Navbar: FC<NavbarProps> = ({ collapsed = false }) => {
  const location = useLocation()
  const user = useAppSelector((state) => state.user.userInfo)
  const { t } = useTranslation()
  const billingPortal = useBillingPortal()
  const selectedCategories = useAppSelector(selectSelectedCategories)
  const categoriesLoading = useAppSelector(selectCategoriesLoading)

  const navSections = getTranslatedNavSections(t, billingPortal, {
    // While the home query resolves the sections stay hidden — never flash
    // categories the merchant did not choose.
    show3DViewer: !categoriesLoading && selectedCategories.includes("viewer3d"),
    showTryOn: !categoriesLoading && selectedCategories.some(isTryOnCategory),
  })
  const navSectionsFiltered = navSections.filter(
    ({ groups }) => !groups?.length || hasAnyRequiredGroup(user.groups, groups)
  )

  const renderItemContent = (link: INavSectionsItems) => {
    const icon = link.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : link.icon

    if (collapsed) return <span>{icon}</span>

    return (
      <>
        <span className="opacity-80 group-hover:opacity-100">{icon}</span>
        <span className="truncate">{link.label}</span>
      </>
    )
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-full flex-col border-r bg-card">
        {/* Logo */}
        <div
          data-testid="app-sidebar-brand"
          className={cn(
            "flex items-center gap-3 px-4 py-4 transition-all duration-300",
            collapsed && "justify-center px-2"
          )}
        >
          <TintMark className="h-8 w-8 shrink-0" />
          {!collapsed && (
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold tracking-tight">
                {t("nav.dashboardTitle")}
              </div>
              <div className="truncate text-[11px] text-foreground/70">{t("nav.workplace")}</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 pb-2" data-testid="app-nav">
          {navSectionsFiltered.map(({ id, title, items }, sectionIndex) => {
            const sectionKey = title || items.map(({ to, label }) => `${to}-${label}`).join("|")
            const activeNavPath = findActiveNavPath(location.pathname, items)

            return (
              <div className={cn(sectionIndex === 0 ? "mt-1" : "mt-5")} key={sectionKey}>
                {!collapsed && title && (
                  <div className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-foreground/60">
                    {title}
                  </div>
                )}
                {collapsed && sectionIndex > 0 && (
                  <div className="mx-auto mb-1.5 h-px w-6 bg-border" />
                )}
                <div className="space-y-0.5">
                  {id === VIRTUAL_TRY_ON_SECTION_ID && (
                    <NavSectionCategories collapsed={collapsed} />
                  )}
                  {items.map((link) => {
                    const isActive =
                      activeNavPath !== null && link.activePaths.includes(activeNavPath)
                    const itemKey = `${link.to}-${sectionKey}-${link.label}`

                    const linkClasses = cn(
                      "group flex w-full items-center rounded-md transition-colors",
                      collapsed
                        ? "justify-center px-0 py-2"
                        : "gap-2.5 px-2.5 py-[7px] text-[13px]",
                      link.disabled
                        ? "cursor-default text-foreground/45"
                        : isActive
                        ? "bg-accent text-foreground font-semibold"
                        : "font-medium text-foreground/90 hover:bg-accent hover:text-foreground"
                    )

                    const content = renderItemContent(link)

                    if (link.disabled) {
                      if (collapsed)
                        return (
                          <Tooltip key={itemKey}>
                            <TooltipTrigger asChild>
                              <div className={linkClasses} data-testid={link.testId}>
                                {content}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" sideOffset={8}>
                              {`${link.label} — ${t("nav.comingSoon")}`}
                            </TooltipContent>
                          </Tooltip>
                        )

                      return (
                        <div
                          key={itemKey}
                          className={cn(linkClasses, "gap-0")}
                          data-testid={link.testId}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex min-w-0 items-center gap-2.5">{content}</span>
                            </TooltipTrigger>
                            <TooltipContent side="right" sideOffset={8}>
                              {t("nav.comingSoon")}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      )
                    }

                    const linkElement = link.onClick ? (
                      <button
                        key={itemKey}
                        type="button"
                        data-testid={link.testId}
                        onClick={link.onClick}
                        disabled={link.loading}
                        className={cn(linkClasses, "text-left")}
                      >
                        {content}
                      </button>
                    ) : link.to && !isExternalUrl(link.to) && link.target !== "_blank" ? (
                      <RouterLink
                        key={itemKey}
                        to={link.to}
                        className={linkClasses}
                        data-testid={link.testId}
                      >
                        {content}
                      </RouterLink>
                    ) : (
                      <a
                        key={itemKey}
                        href={link.to}
                        target={link.target || "_self"}
                        className={linkClasses}
                        data-testid={link.testId}
                      >
                        {content}
                      </a>
                    )

                    if (collapsed)
                      return (
                        <Tooltip key={itemKey}>
                          <TooltipTrigger asChild>{linkElement}</TooltipTrigger>
                          <TooltipContent side="right" sideOffset={8}>
                            {link.label}
                          </TooltipContent>
                        </Tooltip>
                      )

                    return linkElement
                  })}
                </div>
              </div>
            )
          })}
        </nav>

        {/* Footer */}
        <div
          className={cn("border-t px-2 py-3 space-y-1", collapsed && "flex flex-col items-center")}
        >
          {collapsed ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <LanguagePicker collapsed />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">Language</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <DemoUserButton collapsed />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">Settings</TooltipContent>
              </Tooltip>
            </>
          ) : (
            <>
              <LanguagePicker />
              <DemoUserButton />
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
